import { ApolloError } from "@apollo/client";
import {
  CheckoutErrorCode,
  isCheckoutErrorCode,
} from "@shared/checkout";

export class ClientCheckoutError extends Error {
  readonly checkoutCode: CheckoutErrorCode;

  constructor(checkoutCode: CheckoutErrorCode) {
    super(checkoutCode);
    this.name = "ClientCheckoutError";
    this.checkoutCode = checkoutCode;
  }
}

export function stripeJsCheckoutError(message?: string): ClientCheckoutError {
  const lower = (message ?? "").toLowerCase();
  if (lower.includes("authenticat") || lower.includes("3d secure")) {
    return new ClientCheckoutError(CheckoutErrorCode.ChargeAuthRequired);
  }
  if (!message) {
    return new ClientCheckoutError(CheckoutErrorCode.ChargeFailed);
  }
  return new ClientCheckoutError(CheckoutErrorCode.ChargeDeclined);
}

export function getCheckoutCodeFromError(
  error: unknown,
): CheckoutErrorCode | undefined {
  if (error instanceof ClientCheckoutError) {
    return error.checkoutCode;
  }

  if (error instanceof ApolloError) {
    for (const graphQLError of error.graphQLErrors) {
      const code = graphQLError.extensions?.checkoutCode;
      if (isCheckoutErrorCode(code)) return code;
    }
    if (error.networkError && error.graphQLErrors.length === 0) {
      return CheckoutErrorCode.Network;
    }
  }

  if (error && typeof error === "object") {
    const record = error as {
      graphQLErrors?: Array<{ extensions?: { checkoutCode?: unknown } }>;
      networkError?: unknown;
      checkoutCode?: unknown;
    };
    if (isCheckoutErrorCode(record.checkoutCode)) {
      return record.checkoutCode;
    }
    for (const graphQLError of record.graphQLErrors ?? []) {
      const code = graphQLError.extensions?.checkoutCode;
      if (isCheckoutErrorCode(code)) return code;
    }
  }

  return undefined;
}

export function getCheckoutBannerKey(error: unknown): string {
  const code = getCheckoutCodeFromError(error);
  if (code) return `ERRORS.${code}`;
  return "ORDER_ERROR";
}

export function getCheckoutBannerMessage(
  error: unknown,
  t: (key: string) => string,
): string {
  return t(getCheckoutBannerKey(error));
}
